import naja from 'naja';

export default class FileReviewForm {
    private form: HTMLFormElement | null = null;
    private submitButton: HTMLInputElement | null = null;
    private radios: NodeListOf<HTMLInputElement> | null = null;
    private reasonInputs: NodeListOf<HTMLTextAreaElement> | null = null;

    constructor() {
        this.init();
        this.bindNajaEvents();
    }

    private init(): void {
        this.form = document.querySelector('.file-review-form-main');
        if (!this.form) {
            console.log('FileReviewForm: Form not found');
            return;
        }

        this.submitButton = this.form.querySelector('input[type="submit"]');
        this.radios = document.querySelectorAll('.file-review-radio');
        this.reasonInputs = document.querySelectorAll('.file-review-reason');

        console.log('FileReviewForm: Initialized with', this.radios?.length, 'radios and', this.reasonInputs?.length, 'reason inputs');

        this.bindEvents();
        this.validateForm();
    }

    private bindEvents(): void {
        if (!this.radios) {
            return;
        }

        // Handle radio button changes
        this.radios.forEach((radio) => {
            radio.addEventListener('change', () => this.handleRadioChange(radio));
        });

        // Handle reason textarea changes
        if (this.reasonInputs) {
            this.reasonInputs.forEach((textarea) => {
                this.bindTextareaEvents(textarea);
            });
        }

        // Prevent form submission if validation fails
        if (this.form) {
            this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
        }
    }

    private bindTextareaEvents(textarea: HTMLTextAreaElement): void {
        // Remove existing listeners to avoid duplicates
        textarea.removeEventListener('input', this.validateFormBound);
        textarea.removeEventListener('blur', this.validateFormBound);
        
        // Add new listeners
        textarea.addEventListener('input', this.validateFormBound);
        textarea.addEventListener('blur', this.validateFormBound);
    }

    private validateFormBound = (): void => {
        this.validateForm();
    }

    private handleRadioChange(radio: HTMLInputElement): void {
        const fileId = radio.dataset.fileId;
        if (!fileId) {
            return;
        }

        const reasonGroup = document.getElementById(`reason-group-${fileId}`);
        if (!reasonGroup) {
            return;
        }

        const reasonInput = reasonGroup.querySelector('textarea') as HTMLTextAreaElement;
        if (!reasonInput) {
            return;
        }

        if (radio.value === 'not_accepted' && radio.checked) {
            reasonGroup.style.display = 'block';
            reasonInput.setAttribute('required', 'required');
            reasonInput.value = reasonInput.value.trim();
            
            // Add event listeners if not already added
            this.bindTextareaEvents(reasonInput);
        } else if (radio.value === 'accepted' && radio.checked) {
            reasonGroup.style.display = 'none';
            reasonInput.removeAttribute('required');
            reasonInput.value = '';
        }

        // Force validation after DOM changes
        setTimeout(() => this.validateForm(), 10);
    }

    private handleFormSubmit(e: Event): void {
        if (!this.isFormValid()) {
            e.preventDefault();
            e.stopPropagation();
            this.showValidationErrors();
        }
    }

    private validateForm(): void {
        const isValid = this.isFormValid();
        
        console.log('FileReviewForm: Validation result:', isValid);

        if (this.submitButton) {
            if (isValid) {
                this.submitButton.removeAttribute('disabled');
                this.submitButton.classList.remove('disabled');
                console.log('FileReviewForm: Submit button enabled');
            } else {
                this.submitButton.setAttribute('disabled', 'disabled');
                this.submitButton.classList.add('disabled');
                console.log('FileReviewForm: Submit button disabled');
            }
        }
    }

    private isFormValid(): boolean {
        const fileIds = this.getFileIds();
        console.log('FileReviewForm: Validating files:', fileIds);

        for (const fileId of fileIds) {
            // Check if radio is selected
            const statusRadios = document.querySelectorAll<HTMLInputElement>(`input[name="status_${fileId}"]`);
            const selectedRadio = Array.from(statusRadios).find(radio => radio.checked);

            console.log(`FileReviewForm: File ${fileId} - Selected radio:`, selectedRadio?.value);

            if (!selectedRadio) {
                console.log(`FileReviewForm: File ${fileId} - No radio selected`);
                return false;
            }

            // If "not_accepted" is selected, check if reason is provided
            if (selectedRadio.value === 'not_accepted') {
                const reasonTextarea = document.querySelector<HTMLTextAreaElement>(`textarea[name="reason_${fileId}"]`);
                const reasonValue = reasonTextarea?.value.trim() || '';
                console.log(`FileReviewForm: File ${fileId} - Reason value: "${reasonValue}"`);
                
                if (!reasonTextarea || reasonValue === '') {
                    console.log(`FileReviewForm: File ${fileId} - Missing reason for rejection`);
                    return false;
                }
            }
        }

        console.log('FileReviewForm: All validations passed');
        return true;
    }

    private getFileIds(): string[] {
        if (!this.radios) {
            return [];
        }

        const fileIds = new Set<string>();

        this.radios.forEach((radio) => {
            if (radio.dataset.fileId) {
                fileIds.add(radio.dataset.fileId);
            }
        });

        return Array.from(fileIds);
    }

    private showValidationErrors(): void {
        const fileIds = this.getFileIds();

        fileIds.forEach((fileId) => {
            const statusRadios = document.querySelectorAll<HTMLInputElement>(`input[name="status_${fileId}"]`);
            const selectedRadio = Array.from(statusRadios).find(radio => radio.checked);

            // Remove previous error messages
            const existingError = document.querySelector(`#error-status-${fileId}`);
            if (existingError) {
                existingError.remove();
            }

            if (!selectedRadio) {
                // Show error for missing radio selection
                const radioContainer = statusRadios[0]?.closest('.base-cart-checkbox');
                if (radioContainer) {
                    const errorDiv = document.createElement('div');
                    errorDiv.id = `error-status-${fileId}`;
                    errorDiv.className = 'text-danger small mt-1';
                    errorDiv.textContent = 'Vyberte prosím schválení nebo neschválení';
                    radioContainer.appendChild(errorDiv);
                }
            } else if (selectedRadio.value === 'not_accepted') {
                const reasonTextarea = document.querySelector<HTMLTextAreaElement>(`textarea[name="reason_${fileId}"]`);
                if (!reasonTextarea || reasonTextarea.value.trim() === '') {
                    // Show error for missing reason
                    const reasonContainer = reasonTextarea?.closest('.reason-group');
                    if (reasonContainer) {
                        const existingReasonError = reasonContainer.querySelector('.text-danger');
                        if (!existingReasonError) {
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'text-danger small mt-1';
                            errorDiv.textContent = 'Při neschválení je nutné uvést důvod';
                            reasonContainer.appendChild(errorDiv);
                        }
                    }
                }
            }
        });

        // Scroll to first error
        const firstError = document.querySelector('.text-danger');
        if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    private bindNajaEvents(): void {
        naja.snippetHandler.addEventListener('afterUpdate', (event: { detail: { snippet: { id: string } } }) => {
            if (event.detail.snippet.id === 'snippet-fileReviewForm') {
                this.init();
            }
        });
    }
}